import type React from 'react';
import { useMemo, useState } from 'react';

import { getUserData } from '../../../constants/global';
import { useGlobalContext } from '../../../context/global';
import { fmt } from '../../utils/number';
import { AlertSVG, ArrowRightSVG, CheckSVG, RefreshCWSVG, SearchSVG } from '../../utils/svgs';
import { findAnswer, setAnswer, tallyPresence } from './agent-presence';
import { AllPartiesNote } from './all-parties-note';
import { useAutoLoadParties } from './hooks';
import { PartyAvatar } from './party-avatar';
import { getPartyColor } from './party-colors';
import { PresenceBulkBar } from './presence-bulk-bar';
import { PresenceToggle } from './presence-toggle';
import { useAgentPresenceSeed, usePresenceEnabled } from './use-agent-presence';
import { type IPartyVotes, type IVoteCount, sortForDisplay } from './utils';
import { computeVoteBalance, subtotalOf } from './vote-balance';
import { VoteBalanceStrip } from './VoteBalance';

const PartyVotes = () => {
	const {
		global: {
			state: {
				partyVotes,
				partyBallot,
				childCodes,
				selectedChildCode,
				selectedParentCode,
				resultType,
				resultSummary,
				agentPresence,
			},
			updateState,
		},
		onStage,
		getCodeProps,
		states,
	} = useGlobalContext();

	const [searchQuery, setSearchQuery] = useState('');
	const isCode = resultType !== 'EC8A';

	const updateChildCode = () => {
		const p = childCodes.map((i) => ({
			...i,
			status: i?.code === selectedChildCode?.code ? true : i.status,
		}));
		updateState('childCodes', [...p]);
	};

	const onPartyVoteCompleted = () => {
		if (isCode) {
			updateState('partyVoteStage', 'Child');
			updateChildCode();
		} else {
			onStage('Preview');
		}
	};

	const id = selectedChildCode?.codeId || selectedParentCode?.codeId;

	const partyVote: IPartyVotes = id ? partyVotes.filter((i) => i.id === id)[0] : { id: 0, votes: [], name: '', code: '' };

	const { handleCodes, load } = getCodeProps;
	const hasParties = !!partyVote && partyVote.votes.length > 0;

	const { status, retry } = useAutoLoadParties({
		id: Number(id) || 0,
		hasParties,
		handleCodes,
		load,
		// The shared thunk never dispatches `get_PartyError` (network failures
		// only raise a global toast), so infer failure from the raw party list:
		// if it's still empty once retries are exhausted, the fetch failed.
		hasError: !states?._party?.get_Party?.data?.length,
	});

	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { id, value } = e.target;
		const p: IPartyVotes[] = partyVotes.map((partyVoteItem) => ({
			...partyVoteItem,
			votes:
				partyVote.id === partyVoteItem.id
					? partyVoteItem.votes.map((vote) => ({
							...vote,
							// Guard the parse: clearing the input yields NaN, which would
							// poison every running total downstream of it.
							votes: vote.partyId + '' === id ? (isNaN(parseInt(value)) ? 0 : parseInt(value)) : vote.votes,
						}))
					: partyVoteItem.votes,
		}));
		updateState('partyVotes', [...p]);
	};

	// This entity's own subtotal. At EC8A it IS the whole count; at collation
	// levels it is one child's contribution and must never be compared against
	// the parent's declared Valid Votes on its own.
	const totalVotes = useMemo(() => subtotalOf(partyVote), [partyVote?.votes]);

	const filledCount = useMemo(() => partyVote?.votes?.filter((v) => parseInt(v.votes + '') > 0).length || 0, [partyVote?.votes]);

	const totalParties = partyVote?.votes?.length || 0;
	const progressPct = totalParties > 0 ? (filledCount / totalParties) * 100 : 0;
	const isComplete = filledCount === totalParties && totalParties > 0;

	// The declared figure from the Result Summary step, carried forward so the
	// agent can see both numbers while typing rather than discovering the gap at
	// Preview. Always computed from the GRAND total across every entity.
	const balance = useMemo(
		() => computeVoteBalance({ partyVotes, resultSummary, childCodes, resultType }),
		[partyVotes, resultSummary, childCodes, resultType]
	);

	const filteredVotes = useMemo(() => {
		// Ballot order for a real ballot, alphabetical sheet order otherwise. Search only
		// filters this list, and the inputs' tab order follows it because it is DOM order.
		const sorted = sortForDisplay(partyVote?.votes || [], partyBallot);
		if (!searchQuery.trim()) return sorted;
		const q = searchQuery.toLowerCase();
		return sorted.filter((v) => v.label.toLowerCase().includes(q) || (v.longName || '').toLowerCase().includes(q));
	}, [partyVote?.votes, partyBallot, searchQuery]);

	const hasVotes = (v: IVoteCount) => parseInt(v.votes + '') > 0;

	// Polling agents (EC8A only, behind a flag). The roster is the ballot's own party list.
	const presenceOn = usePresenceEnabled();
	const rosterIds = useMemo(() => (partyVote?.votes || []).map((v) => v.partyId), [partyVote?.votes]);
	const { readStatus } = useAgentPresenceSeed(rosterIds);
	const presence = useMemo(() => tallyPresence(agentPresence, rosterIds), [agentPresence, rosterIds]);
	const ownPartyId = Number(getUserData().user?.PartyId) || 0;
	const updatePresence = (partyId: number, patch: Parameters<typeof setAnswer>[2]) =>
		updateState('agentPresence', setAnswer(agentPresence, partyId, patch));

	return (
		<div className='pv-container'>
			{/* Header row */}
			<div className='f-row justify-content-between align-items-center'>
				<div className='f-row-17 hw-mx align-items-center'>
					<p className='m-0 text-little color-label'>Input the vote count for each party.</p>
				</div>
				<button
					type='button'
					className={`pv-refresh ${load ? 'pv-refresh--loading' : ''}`}
					onClick={retry}
					disabled={load}
					aria-label='Refresh party list'
				>
					<RefreshCWSVG
						width='16'
						height='16'
					/>
					<span>{load ? 'Refreshing…' : 'Refresh'}</span>
				</button>
			</div>

			{hasParties && (
				<>
					<AllPartiesNote source={partyBallot.source} />

					{/* Search bar */}
					<div className='pv-search'>
						<SearchSVG />
						<input
							type='text'
							className='pv-search__input'
							placeholder='Search parties...'
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>
						{searchQuery && (
							<button
								type='button'
								className='pv-search__clear'
								onClick={() => setSearchQuery('')}
							>
								Clear
							</button>
						)}
					</div>

					{/* Progress bar */}
					<div className='pv-progress'>
						<div className='pv-progress__bar'>
							<div
								className={`pv-progress__fill ${isComplete ? 'pv-progress__fill--complete' : ''}`}
								style={{ width: `${progressPct}%` }}
							/>
						</div>
						<span className='pv-progress__label'>
							{filledCount} of {totalParties} parties
							{presenceOn ? ` · ${presence.answered} agents recorded` : ''}
						</span>
					</div>

					{presenceOn && (
						<PresenceBulkBar
							rosterIds={rosterIds}
							tally={presence}
							readStatus={readStatus}
						/>
					)}
				</>
			)}

			{/* Main content area */}
			<div className='f-column-33 w-100'>
				{!hasParties && status === 'loading' && (
					<div
						className='pv-grid'
						aria-busy='true'
						aria-label='Loading parties'
					>
						{Array.from({ length: 6 }).map((_, index) => (
							<div
								key={index}
								className='pv-skeleton-card'
								style={{ animationDelay: `${index * 80}ms` }}
							>
								<div className='pv-skeleton-card__header'>
									<span className='pv-skeleton-card__avatar' />
									<span className='pv-skeleton-card__lines'>
										<span className='pv-skeleton-card__line pv-skeleton-card__line--short' />
										<span className='pv-skeleton-card__line' />
									</span>
								</div>
								<span className='pv-skeleton-card__input' />
							</div>
						))}
					</div>
				)}

				{!hasParties && (status === 'empty' || status === 'error') && (
					<div className={`pv-empty ${status === 'error' ? 'pv-empty--error' : ''}`}>
						<span className='pv-empty__icon'>
							<AlertSVG />
						</span>
						<p className='pv-empty__title'>{status === 'error' ? "Couldn't load the party list" : 'No parties loaded yet'}</p>
						<p className='pv-empty__text'>
							{status === 'error'
								? 'Something went wrong while fetching the parties. Check your connection and try again.'
								: 'The party list is empty. Tap below to load it.'}
						</p>
						<button
							type='button'
							className='pv-empty__cta'
							onClick={retry}
						>
							<RefreshCWSVG
								width='16'
								height='16'
							/>
							<span>Try again</span>
						</button>
					</div>
				)}

				{hasParties && !filteredVotes.length && (
					<div className='pv-empty pv-empty--search'>
						<span className='pv-empty__icon'>
							<SearchSVG />
						</span>
						<p className='pv-empty__title'>No parties match your search</p>
						<button
							type='button'
							className='pv-empty__cta pv-empty__cta--ghost'
							onClick={() => setSearchQuery('')}
						>
							Clear search
						</button>
					</div>
				)}

				{hasParties && filteredVotes.length > 0 && (
					<div className='pv-grid'>
						{filteredVotes.map((vote, index) => {
							const color = getPartyColor(vote.label);
							const filled = hasVotes(vote);
							const agent = presenceOn ? findAnswer(agentPresence, vote.partyId) : undefined;
							return (
								<div
									key={vote.partyId}
									className={`pv-card ${filled ? 'pv-card--filled' : ''}${presenceOn ? ` pv-card--agent-${agent?.answer ?? 'unrecorded'}` : ''}`}
									style={
										{
											'animationDelay': `${index * 30}ms`,
											'--party-color': color.bg,
											'--party-color-light': color.text,
										} as React.CSSProperties
									}
								>
									<div className='pv-card__header'>
										<PartyAvatar vote={vote} />
										<div className='pv-card__info'>
											<span
												className='pv-card__abbr'
												style={{ backgroundColor: color.bg, color: color.text }}
											>
												{vote.label}
											</span>
											{vote.longName && <p className='pv-card__name'>{vote.longName}</p>}
										</div>
									</div>
									<div className='pv-card__input-wrap'>
										<input
											type='text'
											inputMode='numeric'
											pattern='[0-9]*'
											id={vote.partyId + ''}
											className={`pv-card__input ${filled ? 'pv-card__input--filled' : ''}`}
											value={vote.votes || 0}
											placeholder='0'
											onClick={(e) => e.currentTarget.select()}
											onChange={handleOnChange}
										/>
										{filled && (
											<span className='pv-card__check'>
												<CheckSVG
													width='10'
													height='10'
													color='#fff'
												/>
											</span>
										)}
									</div>
									{presenceOn && (
										<PresenceToggle
											partyLabel={vote.label}
											answer={agent?.answer ?? 'unrecorded'}
											agentName={agent?.agentName || ''}
											isOwnParty={vote.partyId === ownPartyId}
											onAnswer={(answer) => updatePresence(vote.partyId, { answer })}
											onAgentName={(agentName) => updatePresence(vote.partyId, { agentName })}
										/>
									)}
								</div>
							);
						})}
					</div>
				)}

				{/* Spacer for sticky footer */}
				<div className='pv-footer-spacer' />
			</div>

			{/* Sticky bottom action bar */}
			{hasParties && (
				<div className={`pv-footer ${isComplete ? 'pv-footer--complete' : ''}`}>
					<VoteBalanceStrip
						balance={balance}
						onReview={() => onStage('Party Vote Summary')}
						reviewLabel='Summary'
					/>
					<div className='pv-footer__content'>
						<div className='pv-footer__progress'>
							<div
								className={`pv-footer__progress-fill ${isComplete ? 'pv-footer__progress-fill--complete' : ''}`}
								style={{ width: `${progressPct}%` }}
							/>
						</div>
						<div className='pv-footer__row'>
							<div className='pv-footer__stats'>
								{isCode && (
									<>
										<div className='pv-footer__stat'>
											<span
												className='pv-footer__stat-value'
												key={totalVotes}
											>
												{fmt(totalVotes)}
											</span>
											<span className='pv-footer__stat-label'>This unit</span>
										</div>
										<div className='pv-footer__stat-divider' />
									</>
								)}
								<div className='pv-footer__stat'>
									<span
										className='pv-footer__stat-value'
										key={balance.counted}
									>
										{fmt(balance.counted)} / {fmt(balance.declared)}
									</span>
									<span className='pv-footer__stat-label'>{isCode ? 'All units / Declared' : 'Counted / Declared'}</span>
								</div>
								{!isCode && (
									<>
										<div className='pv-footer__stat-divider' />
										<div className='pv-footer__stat'>
											<span className='pv-footer__stat-value'>
												{filledCount}/{totalParties}
											</span>
											<span className='pv-footer__stat-label'>Parties</span>
										</div>
									</>
								)}
								{!isCode && presenceOn && (
									<>
										<div className='pv-footer__stat-divider' />
										<button
											type='button'
											className='pv-footer__stat pv-footer__stat--link'
											onClick={() => onStage('Polling Agents')}
											aria-label={`Polling agents: ${presence.present} present, ${presence.unrecorded} not recorded. Review all.`}
										>
											<span className='pv-footer__stat-value'>
												{presence.present}/{presence.total}
											</span>
											<span className='pv-footer__stat-label'>Agents ›</span>
										</button>
									</>
								)}
							</div>
							<button
								type='button'
								className={`pv-footer__cta ${isComplete ? 'pv-footer__cta--ready' : ''}`}
								onClick={onPartyVoteCompleted}
							>
								<span>{isCode ? 'Save Votes' : 'Next'}</span>
								<ArrowRightSVG color='#fff' />
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default PartyVotes;
